import React, { useEffect, useState } from "react";
import {
  Container,
  Table,
  Button,
  Spinner,
  Modal,
  Pagination,
  Row,
  Col,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { getOnboardingForms } from "../../redux/slices/onboardingFormSlice";
import Loader from "../../components/Loader";
import Message from "../../components/Message";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faFileAlt,
  faPlus,
  faTrash,
  faUserPlus,
} from "@fortawesome/free-solid-svg-icons";
import { CARD_DESIGN_OPTIONS } from "../../constants";
import Register from "../Register";
import { BACKEND_URL } from "../../config";
import axios from "axios";
import { message } from "antd";
import { Download } from "lucide-react";
import EmptyState from "../../components/EmptyState";

const OnboardingResponses = () => {
  const dispatch = useDispatch();
  const { loading, forms, pagination, error } = useSelector(
    (state) => state.onboardingForm
  );

  const [showModal, setShowModal] = useState(false);
  const [show, setShow] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);

  useEffect(() => {
    dispatch(getOnboardingForms({ page: pagination.page || 1, limit: 10 }));
  }, [dispatch]);

  const handleOpenForm = () => {
    window.open(`${window.location.origin}/client/onboarding`, "_blank");
  };

  const handlePageChange = (page) => {
    dispatch(getOnboardingForms({ page, limit: 10 }));
  };

  const handleAddNew = (form) => {
    setSelectedForm(form);
    setShow(true);
  };

  const openModal = (form) => {
    setSelectedForm(form);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedForm(null);
  };

  const handleDownload = async (qrCodeImage) => {
    if (!qrCodeImage) {
      message.warning("File not found");
      return;
    }
    const downloadMessage = message.loading("Downloading", 0);
    try {
      const response = await axios({
        url: `${BACKEND_URL}api/upload/qrCode?fileName=${encodeURIComponent(
          qrCodeImage
        )}`,
        method: "GET",
        responseType: "blob",
      });
      // Extract the file name from the url
      const extractedFileName = qrCodeImage.split("/").pop();

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", extractedFileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success("Downloaded!");
    } catch (error) {
      message.error("Error downloading the file");
      console.error("Error downloading the file:", error);
    } finally {
      downloadMessage();
    }
  };

  const handleClose = () => setShow(false);

  const styles = {
    shadow: {
      boxShadow: " rgba(100, 100, 111, 0.2) 0px 7px 29px 0px",
    },
    shadowCard: {
      boxShadow:
        " rgba(0, 0, 0, 0.1) 0px 0px 5px 0px, rgba(0, 0, 0, 0.1) 0px 0px 1px 0px",
    },
  };

  return (
    <Row className="p-3 my-3">
      <h4 className="mb-0 text-center text-primary">
        Onboarding Form Responses
      </h4>
      <Row className="my-3">
        <Col xs="auto" className="ms-auto me-0">
          <Button
            variant="primary"
            className="rounded-3"
            onClick={handleOpenForm}
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Create New
          </Button>
        </Col>
      </Row>
      {/* --------------------------------------------------- FORM DETAILS MODAL ----------------------------------------- */}
      <Modal show={showModal} onHide={closeModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title className=" text-primary">
            Onboarding Form Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedForm ? (
            <div className="d-flex flex-column gap-4">
              {/* Basic Details */}
              <div className="p-3 rounded border shadow-sm">
                <h5 className="mb-3 text-secondary">🏨 Basic Details</h5>
                <Row>
                  <Col md={6}>
                    <p>
                      <strong>Hotel:</strong>{" "}
                      {selectedForm.basicDetails.hotelName}
                    </p>
                    <p>
                      <strong>Address:</strong>{" "}
                      {selectedForm.basicDetails.hotelAddress}
                    </p>
                    <p>
                      <strong>Rooms:</strong>{" "}
                      {selectedForm.basicDetails.numberOfRooms}
                    </p>
                  </Col>
                  <Col md={6}>
                    <p>
                      <strong>Contact Person:</strong>{" "}
                      {selectedForm.basicDetails.pointOfContact}
                    </p>
                    <p>
                      <strong>Email:</strong>{" "}
                      {selectedForm.basicDetails.contactEmail}
                    </p>
                    <p>
                      <strong>Start Date:</strong>{" "}
                      {new Date(
                        selectedForm.basicDetails.startDate
                      ).toLocaleDateString()}
                    </p>
                  </Col>
                </Row>
              </div>

              {/* Services */}
              <div className="p-3 rounded border shadow-sm">
                <h5 className="mb-3 text-secondary">🛎️ Services</h5>

                {selectedForm.services.filter((s) => s.enabled).length > 0 ? (
                  <Row className="g-3">
                    {selectedForm.services
                      .filter((s) => s.enabled)
                      .map((s, i) => (
                        <Col md={6} key={i}>
                          <div className="border rounded p-2 bg-light">
                            <p className="mb-1 fw-bold text-primary">
                              {s.department}
                            </p>
                            <p className="mb-0">
                              Employees:{" "}
                              <span className="fw-semibold text-dark">
                                {s.employeeCount || 0}
                              </span>
                            </p>
                          </div>
                        </Col>
                      ))}
                  </Row>
                ) : (
                  <p className="text-muted fst-italic mb-0">
                    No services selected
                  </p>
                )}
              </div>

              {/* Card Design */}
              <div className="p-3 rounded border shadow-sm">
                <h5 className="mb-3 text-secondary">
                  💳 Card Design & Branding
                </h5>
                <Row className="justify-content-between">
                  <Col md={6}>
                    <p>
                      <strong>Option Selected:</strong>{" "}
                      {selectedForm.cardDesign.option
                        ? CARD_DESIGN_OPTIONS[selectedForm.cardDesign.option]
                        : "N/A"}
                    </p>
                    <p>
                      <strong>Promotional Ads:</strong>{" "}
                      {selectedForm.cardDesign.promotionalAds ? "Yes" : "No"}
                    </p>
                    <p>
                      <strong>Custom Chatbot :</strong>{" "}
                      {selectedForm.cardDesign.chatbotInterest}
                    </p>
                  </Col>
                  <Col md={4} className="text-center">
                    {selectedForm.logo ? (
                      <>
                        <p className="">Uploaded Logo</p>
                        <img
                          src={selectedForm.logo}
                          alt="Logo"
                          style={{
                            maxWidth: "150px",
                            maxHeight: "150px",
                            objectFit: "contain",
                          }}
                          className="border rounded p-2 bg-light"
                        />
                        <div className="mt-3">
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => handleDownload(selectedForm.logo)}
                          >
                            Download <Download size={16} />
                          </Button>
                        </div>
                      </>
                    ) : (
                      <p className="text-muted fst-italic">No logo uploaded</p>
                    )}
                  </Col>
                </Row>
              </div>
            </div>
          ) : (
            <p>No details available.</p>
          )}
        </Modal.Body>
      </Modal>

      {/* --------------------------------------------------- ADD NEW MODAL ----------------------------------------- */}
      <Modal
        size="lg"
        show={show}
        onHide={handleClose}
        backdrop="static"
        keyboard={false}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add New Client</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Register handleClose={handleClose} prefillData={selectedForm} />
        </Modal.Body>
      </Modal>

      <Col className="p-3 py-4 rounded-4" style={styles.shadow}>
        {loading ? (
          <Row className="justify-content-center">
            <Col sm="auto">
              <Loader />
            </Col>
          </Row>
        ) : error ? (
          <Row className="justify-content-center">
            <Col sm="auto">
              <Message variant="danger">{error}</Message>
            </Col>
          </Row>
        ) : (
          <>
            {forms.length === 0 ? (
              <EmptyState
                icon={faFileAlt}
                title="No onboarding responses"
                description="No onboarding forms have been submitted yet."
                onButtonClick={handleOpenForm}
              />
            ) : (
              <div className="table-responsive">
                <Table hover responsive className="align-middle">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Date</th>
                      <th>Hotel</th>
                      <th>Contact Person</th>
                      <th>Email</th>
                      <th>Services</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {forms
                      .slice()
                      .sort(
                        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
                      )
                      .map((form, index) => (
                        <tr key={form._id}>
                          <td>{index + 1}.</td>
                          <td>
                            {new Date(form.createdAt).toLocaleDateString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              }
                            )}
                          </td>
                          <td className="fw-semibold text-primary">
                            {form.basicDetails.hotelName}
                          </td>
                          <td>{form.basicDetails.pointOfContact}</td>
                          <td className="text-success">
                            {form.basicDetails.contactEmail}
                          </td>
                          <td>
                            {form.services
                              .filter((s) => s.enabled)
                              .map((s) => (
                                <span
                                  key={s.department}
                                  className="badge bg-light text-dark border me-1"
                                >
                                  {s.department}
                                </span>
                              ))}
                          </td>
                          <td className="text-center">
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id={`tooltip-details-${form._id}`}>
                                  Add as Client
                                </Tooltip>
                              }
                            >
                              <Button
                                variant="outline-success"
                                className="btn-sm m-1"
                                onClick={() => handleAddNew(form)}
                              >
                                <FontAwesomeIcon icon={faUserPlus} size="sm" />
                              </Button>
                            </OverlayTrigger>

                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id={`tooltip-details-${form._id}`}>
                                  View Details
                                </Tooltip>
                              }
                            >
                              <Button
                                variant="outline-primary"
                                className="btn-sm m-1"
                                onClick={() => openModal(form)}
                              >
                                <FontAwesomeIcon icon={faFileAlt} />
                              </Button>
                            </OverlayTrigger>

                            {/* <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-details-${form._id}`}>
                                Delete
                              </Tooltip>
                            }
                          >
                            <Button
                              variant="outline-danger"
                              className="btn-sm m-1"
                              //   onClick={() => handleDelete(form)}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </Button>
                          </OverlayTrigger> */}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </div>
            )}
          </>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="d-flex justify-content-center mt-3">
            <Pagination>
              {[...Array(pagination.pages).keys()].map((x) => (
                <Pagination.Item
                  key={x + 1}
                  active={x + 1 === pagination.page}
                  onClick={() => handlePageChange(x + 1)}
                >
                  {x + 1}
                </Pagination.Item>
              ))}
            </Pagination>
          </div>
        )}
      </Col>
    </Row>
  );
};

export default OnboardingResponses;
